import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useState, useEffect, useRef } from "react";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import Feather from "@expo/vector-icons/Feather";
import { useTranslation } from "react-i18next";
import MacroDescriptionView from "./MacroDescriptionView";
import WeekCalendar from "./WeekCalendar";
import CalendarBlockRowView from "./CalendarBlockRowView";
import TutorialLinkSection from "../../Components/TutorialLinkSection";
import { UISections } from "../../../Resources/Constants";
import { shouldShowProtocolSlotUi } from "../../platform/guards/coachBlockDashboardVisibility";

const ClientCalendar = observer(({ client }) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const blocksWithoutDate = client.blocksWithoutDate;
  const shouldShowBlockDashboardSection =
    client?.coach?.shouldShowBlockDashboardSection ?? true;
  const shouldUseCalendarDashboardInteractions = shouldShowProtocolSlotUi(
    client?.coach,
  );

  const YearPicker = observer(() => {
    const ArrowButton = ({ bool }) => {
      const [isHovered, setIsHovered] = useState(false);
      return (
        <TouchableOpacity
          onPress={() =>
            client.setProperty(
              "weekCalendarYear",
              bool ? client.weekCalendarYear + 1 : client.weekCalendarYear - 1
            )
          }
          style={{ alignItems: "center", padding: 2 }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Feather
            name={bool ? "chevron-right" : "chevron-left"}
            size={20}
            color={isHovered ? Colors.darkgrayColor : Colors.grayColor}
          />
        </TouchableOpacity>
      );
    };

    return (
      <View
        style={{
          flexDirection: "row",
          marginBottom: screenSize === "small" ? 8 : 16,
          alignItems: "center",
          height: 30,
        }}
      >
        <ArrowButton bool={false} />
        <Text
          style={[
            {
              marginBottom: 1,
              fontSize: 18,
              fontWeight: "600",
              width: 90,
              textAlign: "center",
            },
          ]}
        >
          {client.weekCalendarYear}
        </Text>
        <ArrowButton bool={true} />
      </View>
    );
  });

  /* BLOCK & MACRO ROWS  */
  const BlocksWithoutDate = observer(() => {
    // Build data for BlockSegmentView
    const noDateBlocksegments = blocksWithoutDate.map((block) => ({
      block,
      span: block.effectiveTotalWeeks ?? 5,
      key: block.id,
    }));
    const blockColorMap = React.useMemo(() => {
      const byId = client.blockColorMapById || {};
      const macro = client.macroColorMap || {};
      return Object.fromEntries(
        blocksWithoutDate.map((block) => {
          const label = block?.label ?? "";
          const color = macro[label] || "rgba(63, 123, 141, 0.20)";
          return [block.id, color];
        })
      );
    }, [blocksWithoutDate, client.blockColorMapById, client.macroColorMap]);

    return (
      <View
        style={{
          backgroundColor: Colors.whiteColor,
          padding: screenSize === "small" ? 8 : 16,
          borderRadius: 14,
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
        }}
      >
        <Text
          style={{ alignSelf: "flex-start", fontSize: 15, fontWeight: "bold" }}
        >
          {t("blockWithoutDate-string")}
        </Text>
        <View
          style={{
            marginTop: 12,
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {noDateBlocksegments.map((seg) => (
            <CalendarBlockRowView
              key={seg.key}
              seg={seg}
              blockColorMap={blockColorMap}
              onBlockPress={onBlockPress}
            />
          ))}
        </View>
      </View>
    );
  });

  const onBlockPress = (seg) => {
    if (!shouldUseCalendarDashboardInteractions) {
      client.setProperty("selectedMacro", seg.block.label);
      client.setSelectedBlock(seg.block);
      seg.block.setProperty("isShowingDashboard", false);
      client.setProperty("selectedTab", "planification-string");
      return;
    }

    client.setProperty("selectedMacro", seg.block.label);
    const macroBlocks = client.selectedMacroBlocks || [];
    const indexToScroll = macroBlocks.findIndex((b) => b.id === seg.block.id);

    if (indexToScroll >= 0) {
      // Compute pixel offset to scroll a ScrollView to show the selected macro block
      let offset = 0;
      for (let i = 0; i < indexToScroll; i++) {
        const block = macroBlocks[i];
        const blockWidth = block.getBlockDashboardWidth;
        offset += blockWidth + 16; // include row gap
      }

      setTimeout(() => {
        client?.scrollRef.scrollTo({ x: offset, animated: true });
      }, 50);
    }
  };

  return (
    <View style={{ flex: 1, paddingTop: screenSize === "small" ? 16 : 24 }}>
      <View>
        {client && (
          <>
            <YearPicker />
            {client?.coach?.shouldShowUISectionInfo(UISections.CALENDAR_V1) &&
              shouldShowBlockDashboardSection &&
              !client.coach.hideBlockDashboard && (
                <TutorialLinkSection
                  text="tutorialCalendarMacrocycle-string"
                  urlPath="/calendar/sequence-blocks"
                  // marginTop={8}
                  marginBottom={16}
                  showCloseButton={true}
                  onClose={async () =>
                    await client.coach.markUISectionAsShown(
                      UISections.CALENDAR_V1
                    )
                  }
                />
              )}
            {(!shouldShowBlockDashboardSection ||
              client.coach.hideBlockDashboard) && (
              <Text
                style={{
                  color: Colors.darkgrayColor,
                  fontSize: 13,
                  marginBottom: 12,
                  marginTop: -8,
                }}
              >
                {t("planBlocksSequence-string")}
              </Text>
            )}
            <WeekCalendar client={client} />
            {client.showingBlocksWithoutDate &&
              blocksWithoutDate.length > 0 && (
                <View style={{ marginTop: 8 }}>
                  <BlocksWithoutDate />
                </View>
              )}
            {shouldShowBlockDashboardSection && !client.coach.hideBlockDashboard && (
              <MacroDescriptionView client={client} />
            )}
          </>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({});

export default ClientCalendar;
